// TODO: legal review required before public launch.
// Fill in [COMPANY_NAME], [GOVERNING_LAW_COUNTRY], [JURISDICTION_CITY],
// remaining placeholders in the translation files (legal.terms.*).

import { useTranslation } from "react-i18next";

import { LegalPageLayout, LegalSection } from "@/components/layout/LegalPageLayout";

export default function Terms() {
  const { t } = useTranslation();

  return (
    <LegalPageLayout
      title={t("legal.terms.title", { defaultValue: "Terms of Service" })}
      lastUpdated={t("legal.terms.lastUpdated", { defaultValue: "2026-05-18" })}
    >
      <p>
        {t("legal.terms.intro", {
          defaultValue:
            "These Terms of Service ('Terms') govern your access to and use of the DeutschGenie website and services (the 'Service'), operated by [COMPANY_NAME]. By creating an account or otherwise using the Service, you accept these Terms.",
        })}
      </p>

      <LegalSection title={t("legal.terms.sections.acceptance.title", { defaultValue: "1. Acceptance" })}>
        <p>
          {t("legal.terms.sections.acceptance.body", {
            defaultValue:
              "If you do not agree to these Terms, you must not use the Service. By clicking 'Sign up' or continuing to use the Service, you confirm that you are at least 16 years old and capable of entering into a binding agreement.",
          })}
        </p>
      </LegalSection>

      <LegalSection title={t("legal.terms.sections.account.title", { defaultValue: "2. Your Account" })}>
        <p>
          {t("legal.terms.sections.account.body", {
            defaultValue:
              "You are responsible for safeguarding your login credentials, keeping your account information accurate, and for all activity under your account unless caused by our mandatory legal responsibility. You must notify us immediately of any unauthorised access.",
          })}
        </p>
      </LegalSection>

      <LegalSection title={t("legal.terms.sections.acceptableUse.title", { defaultValue: "3. Acceptable Use" })}>
        <p>
          {t("legal.terms.sections.acceptableUse.intro", { defaultValue: "You agree not to:" })}
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>{t("legal.terms.sections.acceptableUse.items.illegal", { defaultValue: "use the Service for any unlawful purpose or in breach of applicable law;" })}</li>
          <li>{t("legal.terms.sections.acceptableUse.items.abuse", { defaultValue: "harass, threaten, or impersonate other users (including via the Partner Finder);" })}</li>
          <li>{t("legal.terms.sections.acceptableUse.items.thirdPartyData", { defaultValue: "submit personal data, confidential information, or protected materials belonging to others unless you have a lawful basis and all required permissions;" })}</li>
          <li>{t("legal.terms.sections.acceptableUse.items.scrape", { defaultValue: "automatically scrape, copy, or redistribute exam content, questions, or AI-generated feedback;" })}</li>
          <li>{t("legal.terms.sections.acceptableUse.items.security", { defaultValue: "attempt to bypass account access rules, usage restrictions, or protective measures;" })}</li>
          <li>{t("legal.terms.sections.acceptableUse.items.malware", { defaultValue: "upload malicious content or interfere with the operation of the Service;" })}</li>
          <li>{t("legal.terms.sections.acceptableUse.items.share", { defaultValue: "share your account with others or resell access to the Service." })}</li>
        </ul>
      </LegalSection>

      <LegalSection title={t("legal.terms.sections.aiDisclaimer.title", { defaultValue: "4. AI-Generated Content Disclaimer" })}>
        <p>
          {t("legal.terms.sections.aiDisclaimer.body", {
            defaultValue:
              "The Service uses external AI service providers to analyse answers and generate practice feedback. AI-generated output may contain errors, inaccuracies, or invented information and is not legal, professional, immigration, academic, or examination advice. You use and rely on feedback at your own risk, and DeutschGenie does not guarantee admission, certification, examination success, language-level progress, or any other result.",
          })}
        </p>
      </LegalSection>

      <LegalSection title={t("legal.terms.sections.ip.title", { defaultValue: "5. Intellectual Property" })}>
        <p>
          {t("legal.terms.sections.ip.body", {
            defaultValue:
              "All content provided through the Service — including exam materials, grammar lessons, software, and the user interface — is the property of [COMPANY_NAME] or its licensors and is protected by copyright and other intellectual-property laws. You receive a limited, personal, non-transferable, non-exclusive licence to use the Service for your own preparation. Trademarks (Goethe-Institut, ÖSD, telc) are the property of their respective owners.",
          })}
        </p>
      </LegalSection>

      <LegalSection title={t("legal.terms.sections.userContent.title", { defaultValue: "6. Your Content" })}>
        <p>
          {t("legal.terms.sections.userContent.body", {
            defaultValue:
              "You retain ownership of content you submit, such as exam answers, audio recordings, Partner Finder profile information, and chat messages sent to other users. You are solely responsible for ensuring that your content is lawful, accurate, appropriate, and that you have the rights and permissions needed to submit it. You grant us a limited, worldwide, royalty-free licence to host, transmit, encrypt, store, and process such content only as necessary to operate and secure the Service, including sharing it with the external service providers described in our Privacy Policy. We are not obliged to monitor user content, including chat messages, except where required by law.",
          })}
        </p>
      </LegalSection>

      <LegalSection title={t("legal.terms.sections.partnerChat.title", { defaultValue: "7. Partner Finder and User-to-User Chat" })}>
        <p>
          {t("legal.terms.sections.partnerChat.scope", {
            defaultValue:
              "The Partner Finder allows you to make a learning profile discoverable to other users. The in-app chat is an optional, limited, text-only messaging feature offered solely to support language practice between users. The chat does not support attachments, images, voice notes, video, calls, or file sharing. Chat is provided on an as-is and as-available basis and may be limited, rate-restricted, suspended, or discontinued at any time where permitted by law.",
          })}
        </p>
        <p>
          {t("legal.terms.sections.partnerChat.encryption", {
            defaultValue:
              "Chat messages are encrypted in transit and at rest on our servers. We do not actively read, monitor, moderate, or pre-screen private messages, and we do not use chat content for training, profiling, advertising, or analytics. Encryption mitigates but cannot eliminate risk; we make no guarantee of confidentiality against unlawful access, against the recipient of your messages, or against compromise of your own device or account.",
          })}
        </p>
        <p>
          {t("legal.terms.sections.partnerChat.deletion", {
            defaultValue:
              "When you delete a conversation or your account, the associated chat content is removed from our active systems. Short-term encrypted backups, if any, are overwritten in the ordinary backup cycle. We do not offer message recovery, undelete, or restoration of deleted conversations or accounts.",
          })}
        </p>
        <p>
          {t("legal.terms.sections.partnerChat.userConduct", {
            defaultValue:
              "Communications through Partner Finder and chat take place directly between independent users. You acknowledge that other users are independent third parties whom we do not control, verify, vet, or endorse. We are not a party to any conversation, arrangement, transaction, meeting, or relationship that arises between users, and we accept no responsibility for the statements, conduct, accuracy, intentions, identity, age, or safety of other users. Any dispute between users must be resolved between those users.",
          })}
        </p>
        <p>
          {t("legal.terms.sections.partnerChat.prohibited", {
            defaultValue:
              "When using Partner Finder and chat, you must not: (a) send unlawful, harassing, threatening, defamatory, discriminatory, sexual, exploitative, or harmful content; (b) contact minors for any purpose other than language practice consistent with applicable law; (c) send spam, unsolicited advertising, scams, phishing, malware, or links to such content; (d) share special-category personal data (Art. 9 GDPR), payment details, credentials, government IDs, or confidential third-party information; (e) use chat for medical, legal, financial, safety-critical, or commercial purposes; (f) impersonate any person or misrepresent your identity, age, or affiliation; (g) attempt to circumvent the technical limits of the chat (for example by encoding files inside text).",
          })}
        </p>
        <p>
          {t("legal.terms.sections.partnerChat.reporting", {
            defaultValue:
              "If another user breaches these Terms or applicable law, use the in-app reporting and blocking tools, and contact law enforcement where appropriate. We may, but are not obliged to, review reported content and suspend or terminate accounts that violate these Terms. To the maximum extent permitted by law, we disclaim all liability arising from communications between users, including but not limited to offline meetings, financial loss, emotional harm, or unlawful conduct by other users.",
          })}
        </p>
      </LegalSection>

      <LegalSection title={t("legal.terms.sections.payment.title", { defaultValue: "8. Pricing and Payment" })}>
        <p>
          {t("legal.terms.sections.payment.body", {
            defaultValue:
              "Paid plans, if offered, are described on the Plans page. Prices and billing terms applicable to your subscription apply from the date you confirm a plan. If no paid plan is enabled, the Service is provided free of charge and on an as-is basis.",
          })}
        </p>
      </LegalSection>

      <LegalSection title={t("legal.terms.sections.withdrawal.title", { defaultValue: "9. Right of Withdrawal (Consumers in the EU)" })}>
        <p>
          {t("legal.terms.sections.withdrawal.body", {
            defaultValue:
              "If you are a consumer based in the EU and purchase a paid plan, you have a statutory right to withdraw from the contract within 14 days. By starting to use a paid feature immediately, you expressly request that performance begin within the withdrawal period and acknowledge that your right of withdrawal lapses upon full performance (§ 356 (5) BGB).",
          })}
        </p>
      </LegalSection>

      <LegalSection title={t("legal.terms.sections.termination.title", { defaultValue: "10. Termination" })}>
        <p>
          {t("legal.terms.sections.termination.body", {
            defaultValue:
              "You may delete your account at any time. We may suspend or terminate your account if you materially breach these Terms, abuse the Service, or if required by law. Upon termination, your right to use the Service ceases immediately; provisions that by their nature should survive (e.g. intellectual property, liability, governing law) remain in effect.",
          })}
        </p>
      </LegalSection>

      <LegalSection title={t("legal.terms.sections.warranty.title", { defaultValue: "11. Disclaimer of Warranties" })}>
        <p>
          {t("legal.terms.sections.warranty.body", {
            defaultValue:
              "The Service is provided on an 'as is' and 'as available' basis. To the maximum extent permitted by law, we disclaim all express and implied warranties, including merchantability, fitness for a particular purpose, non-infringement, accuracy, availability, and uninterrupted or error-free operation. Features may change, be suspended, or be discontinued where permitted by law. You are responsible for keeping your own copies of important learning materials or submissions where this is important to you.",
          })}
        </p>
      </LegalSection>

      <LegalSection title={t("legal.terms.sections.liability.title", { defaultValue: "12. No Liability to the Extent Permitted by Law" })}>
        <p>
          {t("legal.terms.sections.liability.body", {
            defaultValue:
              "To the maximum extent permitted by applicable law, we do not accept responsibility for indirect, incidental, consequential, special, punitive, or loss-of-profit damages, for missed learning or examination outcomes, for losses caused by relying on AI-generated feedback or by misuse of the Service, or for any harm, loss, or damage arising out of communications between users (including Partner Finder introductions and chat), the conduct of other users, or any offline interactions that result from contacts made through the Service. Nothing in these Terms excludes or limits liability where exclusion is not permitted by law, including liability for intent, gross negligence, injury to life, body, or health, mandatory consumer rights, or liability under the German Product Liability Act.",
          })}
        </p>
      </LegalSection>

      <LegalSection title={t("legal.terms.sections.changes.title", { defaultValue: "13. Changes to these Terms" })}>
        <p>
          {t("legal.terms.sections.changes.body", {
            defaultValue:
              "We may modify these Terms from time to time. Where required by law, or where a material change affects an active paid plan or core Service right, we will notify you before the change takes effect. Continued use of the Service after the effective date constitutes acceptance of the revised Terms.",
          })}
        </p>
      </LegalSection>

      <LegalSection title={t("legal.terms.sections.law.title", { defaultValue: "14. Governing Law and Jurisdiction" })}>
        <p>
          {t("legal.terms.sections.law.body", {
            defaultValue:
              "These Terms are governed by the laws of [GOVERNING_LAW_COUNTRY], without regard to its conflict-of-laws rules. Mandatory consumer-protection provisions of your country of residence remain unaffected. Exclusive place of jurisdiction for disputes with merchants is [JURISDICTION_CITY].",
          })}
        </p>
      </LegalSection>

      <LegalSection title={t("legal.terms.sections.contact.title", { defaultValue: "15. Contact" })}>
        <p>
          {t("legal.terms.sections.contact.body", {
            defaultValue: "Questions about these Terms can be sent to deutschgenie.contact@gmail.com.",
          })}
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
